import { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "lucide-react";
import { FieldShell } from "./FieldShell";
import { fieldIds, inputClass, resolveFieldState } from "./fieldStyles";

interface DateInputProps {
  id: string;
  label: string;
  /** ISO date YYYY-MM-DD or empty string. */
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  hint?: string;
  required?: boolean;
  /** Snap any pick to the first of the month (Effective Date). */
  firstOfMonthOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  containerClassName?: string;
  children?: never;
}

function parseIso(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function toIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function snapToFirst(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Brand-themed react-datepicker; stores ISO strings. Never a native date input. */
export function DateInput({ id, label, value, onChange, error, hint, required, firstOfMonthOnly, minDate, maxDate, containerClassName }: DateInputProps) {
  const ids = fieldIds(id, error, hint);
  const state = resolveFieldState({ error });
  const selected = parseIso(value);
  const filterDate = useMemo(() => (firstOfMonthOnly ? (date: Date) => date.getDate() === 1 : undefined), [firstOfMonthOnly]);

  const handleChange = (date: Date | null) => {
    if (!date) {
      onChange("");
      return;
    }
    onChange(toIso(firstOfMonthOnly ? snapToFirst(date) : date));
  };

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName}>
      <div className="relative">
        <DatePicker
          id={id}
          selected={selected}
          onChange={handleChange}
          filterDate={filterDate}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat="MM/dd/yyyy"
          placeholderText="MM/DD/YYYY"
          autoComplete="off"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          calendarClassName="optimyl-datepicker"
          className={`${inputClass(state)} pr-10`}
          wrapperClassName="w-full"
          ariaInvalid={error ? "true" : undefined}
          ariaDescribedBy={ids.describedBy}
          ariaRequired={required ? "true" : undefined}
          isClearable
        />
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
          <Calendar size={16} aria-hidden="true" />
        </span>
      </div>
    </FieldShell>
  );
}
